import randomUUID from '@/utils/uuid'
import dragFunctions from '../helpers/dragFunctions'
import dataHelpers from '../helpers/dataHelpers'
import positionHelpers from '@/helpers/positionHelpers'
import * as diagramLayout from '@/shared/utils/diagramLayout'

export const workflowApiHelpers = {
  findUpdatedNodes (svgGraph, workflow, svgLanes) {
    const parents = []
    let foundUpdatedNode = false
    const res = {}
    const eventsMap = workflow?.domainEvents?.reduce((map, value) => {
      map[value.id] = value
      return map
    }, {})
    for (const newEventId in svgGraph) {
      const newEvent = svgGraph[newEventId]
      const oldEvent = eventsMap?.[newEventId]
      let updatedFields = {}
      let dirtyFlag = false
      if (!oldEvent) {
        updatedFields = newEvent
        dirtyFlag = true
      } else {
        if (oldEvent.color?.red !== newEvent.color?.red ||
          oldEvent.color?.green !== newEvent.color?.green ||
          oldEvent.color?.blue !== newEvent.color?.blue) {
          updatedFields.color = newEvent.color
          dirtyFlag = true
        }
        if (!equalConditionLabels(oldEvent.conditionLabels, newEvent.conditionLabels)) {
          updatedFields.conditionLabels = newEvent.conditionLabels
          dirtyFlag = true
        }
        if (oldEvent.description !== newEvent.name) {
          updatedFields.name = newEvent.name
          dirtyFlag = true
        }
        if (oldEvent.laneId !== newEvent.laneId) {
          updatedFields.laneId = newEvent.laneId
          dirtyFlag = true
        }
        if (oldEvent.groupId !== newEvent.groupId) {
          updatedFields.groupId = newEvent.groupId
          dirtyFlag = true
        }
        if (oldEvent.offset !== newEvent.offset) {
          updatedFields.offset = newEvent.offset
          dirtyFlag = true
        }
        if (JSON.stringify(oldEvent.subprocess) !== JSON.stringify(newEvent.subprocess)) {
          updatedFields.subprocess = newEvent.subprocess
          dirtyFlag = true
        }
        if (JSON.stringify(oldEvent.parents) !== JSON.stringify(newEvent.parents)) {
          updatedFields.parents = newEvent.parents
          dirtyFlag = true
        }
        if (oldEvent.cards &&
            newEvent.cards &&
            JSON.stringify(oldEvent.cards) !== JSON.stringify(newEvent.cards)) {
          // the only usage of this is when updating card description
          // with custom text (SelectEntityCombobox) and when deleting card and data model
          // in domain model tab (deleteCardAndModel function).
          // Otherwise, the cards are updated only with separate mutation
          updatedFields.cards = newEvent.cards
          dirtyFlag = true
        }
        if (oldEvent.type !== newEvent.type) {
          updatedFields.type = newEvent.type
          dirtyFlag = true
        }
      }
      if (dirtyFlag) {
        updatedFields.version = newEvent.version
        updatedFields.id = newEvent.id
        updatedFields.index = newEvent.index
        // if we are passing in a laneId, then also pass in index for validation
        if (updatedFields.laneId) {
          updatedFields.laneIndex = svgLanes[updatedFields.laneId].index
        }
        res[newEventId] = updatedFields
        parents.push(...newEvent.parents)
        foundUpdatedNode = true
      }
    }
    if (foundUpdatedNode) {
      // make sure to pass along all parents
      // to ensure database transactional consistency
      // only id and version needed for parents that
      // are not being updated
      for (const parent of parents) {
        if (!res[parent] && parent !== 'start') {
          res[parent] = {
            id: eventsMap[parent].id,
            version: eventsMap[parent].version
          }
        }
      }
      return res
    } else {
      return []
    }
  },

  filterCardFields (card) {
    if (!card) {
      return card
    }

    return {
      id: card.id,
      description: card.description,
      releaseNo: card.releaseNo,
      sortkey: card.sortkey,
      cardTypeId: card.cardTypeId,
      done: card.done,
      boundedContext: card.boundedContext,
      creatorId: card.creatorId,
      version: card.version,
      created: card.created,
      schemaId: card.schemaId,
      tags: card.tags,
      aiGenerated: card.aiGenerated
    }
  },

  getIndexesAndEventFromCardId (workflow, cardId) {
    let eventId = null
    let eventIndex = 0
    let cardIndex = 0
    for (const event of workflow.domainEvents) {
      cardIndex = 0
      if (event.cards) {
        for (const card of event.cards) {
          if (card.id === cardId) {
            eventId = event.id
            return { eventId, eventIndex, cardIndex, card }
          }
          cardIndex++
        }
      }
      eventIndex++
    }
  },

  findDeletedEvent (svgGraph, workflow) {
    const svgGraphKeys = Object.keys(svgGraph)
    if (svgGraphKeys?.length < workflow?.domainEvents?.length) {
      for (const event of workflow.domainEvents) {
        if (!svgGraphKeys.includes(event.id)) {
          const index = workflow.domainEvents.findIndex((ev) => ev.id === event.id)
          return {
            deleteIndex: index,
            deleteVersion: workflow.domainEvents[index].version,
            deleteId: workflow.domainEvents[index].id,
            deletedParents: workflow.domainEvents[index].parents
          }
        }
      }
    }
    return {}
  },

  findDeletedEvents (svgGraph, workflow) {
    const svgGraphKeys = Object.keys(svgGraph)
    const deletedEvents = []
    const deletedParents = new Set([])
    if (svgGraphKeys?.length < workflow?.domainEvents?.length) {
      for (const event of workflow.domainEvents) {
        if (!svgGraphKeys.includes(event.id)) {
          const index = workflow.domainEvents.findIndex((ev) => ev.id === event.id)
          deletedEvents.push({
            deleteIndex: index,
            deleteVersion: workflow.domainEvents[index].version,
            deleteId: workflow.domainEvents[index].id
          })
          for (const parent of (workflow.domainEvents[index].parents || [])) {
            deletedParents.add(parent)
          }
        }
      }
    }
    return { deletedEvents, deletedParents }
  },

  addLane (svgLanes, laneName) {
    const laneId = randomUUID()
    if (!svgLanes) { svgLanes = {} }
    const lanesCount = Object.keys(svgLanes).length || 0
    const lastLane = Object.values(svgLanes).find(lane => lane.index === lanesCount - 1)
    const lastLaneId = lastLane?.id || null
    svgLanes[laneId] = {
      id: laneId,
      x: 0,
      y: svgLanes[lastLaneId] ? svgLanes[lastLaneId].y + svgLanes[lastLaneId].height - 15 : 0, // -15 necessary because the last lane is 15px higher to fit color dots
      height: 171, // 171 instead of 156 necessary because the last lane is 15px higher
      name: laneName || 'Lane ' + (lanesCount + 1),
      laneAbove: lastLaneId,
      emptyLane: false,
      index: lastLaneId ? svgLanes[lastLaneId].index + 1 : 0
    }
    if (lastLaneId) {
      svgLanes[lastLaneId].height = svgLanes[lastLaneId].height - 15 // -15 necessary because the last lane is 15px higher
    }
    return laneId
  },

  deleteLane (laneId, svgLanes, svgGraph) {
    if (!svgLanes || !svgLanes[laneId]) { return }

    // if the last lane is deleted, update the height
    // of the new last lane (last lane is 15px higher)
    if (!svgLanes[laneId].laneBelow) {
      const laneAbove = svgLanes[laneId]?.laneAbove
      if (laneAbove) {
        svgLanes[laneAbove].height += 15
      }
    }

    // grab lane objects above and below deleted lane
    let laneAbove = svgLanes[svgLanes[laneId].laneAbove]
    let laneBelow = svgLanes[svgLanes[laneId].laneBelow]

    // delete the lane
    delete svgLanes[laneId]

    if (laneBelow) {
      if (laneAbove) {
        // if there is a lane above, link it to the new lane below
        laneAbove.laneBelow = laneBelow.id
        laneBelow.laneAbove = laneAbove.id
      } else {
        laneBelow.laneAbove = null
      }
      // start from the lane below the deleted lane
      // and iterate downwards, updating y positions
      while (laneBelow) {
        const oldVerticalPosition = laneBelow.y
        laneBelow.y = laneAbove ? (laneAbove.y + laneAbove.height) : 0
        const diff = oldVerticalPosition - laneBelow.y
        // update all nodes in this lane to reflect new lane y position
        Object.values(svgGraph).forEach((node) => {
          if (node.laneId === laneBelow.id) {
            node.y = node.y - diff
          }
        })
        laneAbove = laneBelow
        laneBelow = svgLanes[laneBelow.laneBelow]
      }
    }
  },

  hasGapBetween (svgGraph, currentId, childId, gap = 0) {
    if (svgGraph[currentId] &&
      svgGraph[childId] &&
      ((positionHelpers.getSlotXFromId(childId, svgGraph) - positionHelpers.getSlotXFromId(currentId, svgGraph)) > gap)) {
      return true
    } else {
      return false
    }
  },

  isMainParent (svgGraph, parentId, childId) {
    if (svgGraph[childId]?.parents[0] === parentId) {
      return true
    }
    return false
  },

  isParent (svgGraph, parentId, childId) {
    if (svgGraph[childId]?.parents?.includes(parentId)) {
      return true
    }
    return false
  },

  // special case when the colliding event has another parent that is a gateway
  eventHasAnotherParentThatIsAGateway (svgGraph, event, parentId) {
    for (const id of event.parents) {
      if (id !== parentId &&
        svgGraph[id]?.type === 'bpmn:ExclusiveGateway' &&
        svgGraph[event.id].x >= svgGraph[id].x) {
        return true
      }
    }
    return false
  },

  offsetOccupied (svgGraph, offset, parentId, laneId, eventsSharingSameSpace, isParallelInsertion = false) {
    if (eventsSharingSameSpace?.length > 0) {
      const index = eventsSharingSameSpace.findIndex(event => {
        return event.offset === offset && // look for colliding offset..
          (
            !svgGraph[event.parents[0]] || // ..that doesn't have a parent (start node)
            (
              !(workflowApiHelpers.isParent(svgGraph, parentId, event.id) && svgGraph[parentId]?.type === 'bpmn:Task' && !isParallelInsertion) && // ..that won't move away (a Task parent's child does, unless this is a parallel insert)
              (parentId === 'start' ||
                (svgGraph[parentId] && svgGraph[event.parents[0]] && svgGraph[event.parents[0]].y <= svgGraph[parentId].y)) // ..and the colliding event's parent is higher up or our parent is 'start'
            ) || // or
            workflowApiHelpers.eventHasAnotherParentThatIsAGateway(svgGraph, event, parentId)
          )
      })
      if (index >= 0) {
        return true
      }
    }
    return false
  },

  // find all connections that are not the main connections
  // basically all connections where the parent is not the
  // first parent in the parents array (not parents[0])
  findExtraConnections (svgGraph) {
    const res = []
    const tmpArr = Object.values(svgGraph || {})
    const eventsWithExtraConnections = tmpArr.filter(event => event.parents && event.parents.length > 1)
    for (const eventWithExtraConnections of eventsWithExtraConnections) {
      for (let [index, extraConnectionParentId] of eventWithExtraConnections.parents.entries()) {
        if (index > 0) {
          if (extraConnectionParentId === 'start') {
            extraConnectionParentId = eventWithExtraConnections.id
          }

          res.push({
            parentId: extraConnectionParentId,
            childId: eventWithExtraConnections.id,
            startSlotX: positionHelpers.getSlotXFromId(extraConnectionParentId, svgGraph) + 1,
            endSlotX: positionHelpers.getSlotXFromId(eventWithExtraConnections.id, svgGraph) - 1,
            slotY: svgGraph[extraConnectionParentId].type === 'bpmn:Task'
              ? positionHelpers.getSlotYFromId(extraConnectionParentId, svgGraph)
              : positionHelpers.getSlotYFromId(eventWithExtraConnections.id, svgGraph)
          })
        }
      }
    }
    return res
  },

  // long connections are main connections (parents[0])
  // that are longer than one slot and reach into a new group
  findLongConnections (svgGraph) {
    const res = []
    // start nodes cannot have long main connections into them
    // only the first nodes in a group can have a long main connection
    const nodes = Object.values(svgGraph).filter(node => node.groupId && !node.parents.includes('start'))
    for (const node of nodes) {
      const childSlotX = positionHelpers.getSlotXFromPoint(node.x)
      const parentSlotX = positionHelpers.getSlotXFromPoint(svgGraph[node.parents[0]].x)
      if (childSlotX - parentSlotX > 1) {
        const parentId = node.parents[0]
        res.push({
          parentId,
          childId: node.id,
          startSlotX: parentSlotX + 1,
          endSlotX: childSlotX - 1,
          slotY: svgGraph[parentId].type === 'bpmn:Task' ? positionHelpers.getSlotYFromPoint(svgGraph[parentId].y) : positionHelpers.getSlotYFromPoint(svgGraph[node.id].y)
        })
      }
    }
    return res
  },

  getPathsOccupyingSlot (slotX, slotY, svgGraph) {
    // extra connections
    const extraConnections = workflowApiHelpers.findExtraConnections(svgGraph)
    // long connections crossing group boundaries
    const longConnections = workflowApiHelpers.findLongConnections(svgGraph)
    const collidingExtraConnections = [...extraConnections, ...longConnections].filter(
      connection => {
        return (connection.slotY === slotY) &&
          (connection.startSlotX <= slotX) &&
          (connection.endSlotX >= slotX)
      }
    )
    return collidingExtraConnections
  },

  isNodeCrossedByPath (node, svgGraph) {
    const slotX = positionHelpers.getSlotXFromPoint(node.x)
    const slotY = positionHelpers.getSlotYFromPoint(node.y)
    let pathsOccupyingSlot = workflowApiHelpers.getPathsOccupyingSlot(slotX, slotY, svgGraph)
    pathsOccupyingSlot = pathsOccupyingSlot.filter(path => path.parentId !== node.parentIds?.[0] || (svgGraph[path.parentId].type === 'bpmn:ExclusiveGateway'))
    return pathsOccupyingSlot.length > 0
  },

  createVerticalSpaceForOffset (offset, eventsSharingSameSpace, parentId, svgGraph, svgLanes) {
    let index
    if ((index = eventsSharingSameSpace?.findIndex(
      (event) => {
        return (event.offset === offset) && (!parentId || !workflowApiHelpers.isParent(svgGraph, parentId, event.id))
      }
    )) >= 0) {
      this.createVerticalSpaceForOffset(offset + 1, eventsSharingSameSpace, parentId, svgGraph, svgLanes)
      svgGraph[eventsSharingSameSpace[index].id].offset++
      svgGraph[eventsSharingSameSpace[index].id].y = positionHelpers.getYPointFromOffset(svgGraph[eventsSharingSameSpace[index].id].offset, eventsSharingSameSpace[index].laneId, svgLanes)
      if (!workflowApiHelpers.isNodeWithinLaneBoundaries(svgGraph[eventsSharingSameSpace[index].id], svgLanes)) {
        svgLanes[svgGraph[eventsSharingSameSpace[index].id].laneId].height = (svgGraph[eventsSharingSameSpace[index].id].offset + 1) * 156
        workflowApiHelpers.updateLane(svgLanes[svgGraph[eventsSharingSameSpace[index].id].laneId], svgGraph, svgLanes)
      }
    }
  },

  isNodeWithinLaneBoundaries (node, svgLanes) {
    if (svgLanes[node.laneId]?.height >= (node.offset + 1) * 156) {
      return true
    } else {
      return false
    }
  },

  /**
   * Calculates and updates a node's lane assignment and offset based on its Y position
   * @param {Object} node - The node to update
   * @param {Object} svgLanes - The lanes object
   * @returns {string} - The assigned lane ID
   */
  calculateNodeLaneAndOffset (node, svgLanes) {
    // Find the correct lane by sorting by index
    const laneIds = Object.keys(svgLanes).sort((a, b) => svgLanes[a].index - svgLanes[b].index)
    let assignedLane = laneIds[laneIds.length - 1] // default to last lane

    for (const laneId of laneIds) {
      if ((node.y + 40) < (svgLanes[laneId].y + svgLanes[laneId].height)) {
        assignedLane = laneId
        break
      }
    }

    // Calculate offset within the lane
    node.offset = Math.floor((node.y + 40 - svgLanes[assignedLane].y) / 156)
    if (node.offset < 0) {
      node.offset = 0
    }

    // Snap Y position to the grid
    node.y = svgLanes[assignedLane].y + 30 + node.offset * 156
    node.laneId = assignedLane

    return assignedLane
  },

  /**
   * Calculates the snap delta for an anchor node during multi-selection drag
   * This determines how much the anchor will move when snapped to grid
   * @param {Object} anchorNode - The anchor node (should be a copy, not the original)
   * @param {Object} svgGraph - The graph containing all nodes
   * @param {Object} svgGroups - The groups object
   * @param {Object} svgLanes - The lanes object
   * @returns {Object} - Object containing snapDeltaX and snapDeltaY
   */
  calculateSnapDeltaForAnchor (anchorNode, svgGraph, svgGroups, svgLanes) {
    // Store anchor's position before snapping
    const anchorBeforeX = anchorNode.x
    const anchorBeforeY = anchorNode.y

    // Snap X coordinate based on connections
    const isStartingPoint = (anchorNode.parents || []).findIndex(id => id === 'start') >= 0
    if (anchorNode.groupId && svgGroups[anchorNode.groupId]) {
      anchorNode.x = 80 + svgGroups[anchorNode.groupId].startSlotX * 160
    } else if (anchorNode.parents && anchorNode.parents[0] && !isStartingPoint) {
      anchorNode.x = svgGraph[anchorNode.parents[0]].x + 160
    } else if (isStartingPoint) {
      anchorNode.x = 80
    } else {
      if (anchorNode.childIds && anchorNode.childIds[0]) {
        anchorNode.x = svgGraph[anchorNode.childIds[0]].x - 160
      }
    }

    // Snap Y coordinate to lane and calculate offset
    this.calculateNodeLaneAndOffset(anchorNode, svgLanes)

    // Calculate the snap delta
    const snapDeltaX = anchorNode.x - anchorBeforeX
    const snapDeltaY = anchorNode.y - anchorBeforeY

    return {
      snapDeltaX,
      snapDeltaY,
      snappedX: anchorNode.x,
      snappedY: anchorNode.y
    }
  },

  // can I remove these?
  updateNode (node, svgGraph) {
    if (node) {
      svgGraph[node.id] = node
    }
  },

  // can I remove these?
  updateNodes (laneId, svgGraph, svgLanes) {
    for (const node in svgGraph) {
      if (svgGraph[node].laneId === laneId) {
        const nodeCopy = Object.assign({}, svgGraph[node], { y: svgLanes[laneId].y + 30 + svgGraph[node].offset * 140 })
        workflowApiHelpers.updateNode(nodeCopy, svgGraph)
      }
    }
  },

  updateLane (lane, svgGraph, svgLanes) {
    if (lane) {
      delete svgLanes[lane.id]
      svgLanes[lane.id] = lane
      if (lane.laneBelow) {
        const updatedLane = Object.assign({}, svgLanes[lane.laneBelow], { y: svgLanes[lane.id].y + svgLanes[lane.id].height })
        this.updateLane(updatedLane, svgGraph, svgLanes)
        this.updateNodes(updatedLane.id, svgGraph, svgLanes)
      }
    }
  },

  replaceChild (node, oldChild, newChild) {
    if (node) {
      if (!node.childIds) { node.childIds = [] }
      // remove new child if it already exists on node to avoid adding it twice
      let index = node.childIds.indexOf(newChild?.id)
      if (index >= 0) {
        node.childIds.splice(index, 1)
      }

      // update node with new child
      index = node.childIds.indexOf(oldChild?.id)
      if (index >= 0) {
        if (newChild && newChild.id !== node.id) {
          const newChildId = newChild.id
          const temp = [...node.childIds] // use temp to fix weird bug
          temp.splice(index, 1, newChildId)
          node.childIds = temp
        } else {
          node.childIds.splice(index, 1)
        }
      } else {
        node.childIds.push(newChild.id)
      }
    }
  },

  // takes a svgGraph and svgGroups and returns all paths of the svgGraph
  // a path is a list of connections (or edges)
  // the current path ends and a new path begins when a group boundary is crossed
  // a connection is an object with the following properties:
  // - child: the id of the child node
  // - parent: the id of the parent node
  // - x: the x coordinate of the child node
  findPaths (svgGraph, svgGroups, aggregatedPaths, parentChildMap = null, currentNode = 'start', path = null, pathLengths = {}, currentPathNodes = null, pathStartMap = null) {
    return diagramLayout.findPaths(svgGraph, svgGroups, aggregatedPaths, parentChildMap, currentNode, path, pathLengths, currentPathNodes, pathStartMap)
  },

  // streches out the diagram to the right
  // by caluclating all possible paths and then
  // reaaranging the nodes so the longest paths
  // are used when rendering the diagram
  // parents[0] is the main parent used for rendering
  //
  // remove starting points can not be used when
  // dragging in the diagram, but can be used for
  // all other cases when all events are locked
  // in their positions
  updateEventsWithLongestPath (svgGraph, svgGroups, removeStartingPoints = true) {
    // The start-node cleanup stays here because it depends on frontend-only drag helpers;
    // the longest-path reorder itself is shared with the MCP via diagramLayout.
    if (removeStartingPoints) {
      for (const eventId in svgGraph) {
        if (svgGraph[eventId].parents[0] === 'start') {
          workflowApiHelpers.removeStartingPointIfNotConnected(eventId, svgGraph, svgGroups)
        }
      }
    }

    return diagramLayout.reorderEventsToLongestPath(svgGraph, svgGroups)
  },

  getGroupFromNode (node, svgGroups, svgGraph) {
    return diagramLayout.getGroupFromNode(node, svgGroups, svgGraph)
  },

  // align all nodes in the svgGraph object
  // if a node belongs to a group, then align with the group
  // otherwise align with its main parent
  alignBranches (svgGraph, svgGroups) {
    const startIds = []
    Object.values(svgGraph).forEach(node => {
      if (node.parents[0] === 'start') {
        startIds.push(node.id)
      }
    })
    const movedNodeIds = new Set()
    const processedNodes = new Set()
    // traverse all nodes in the graph
    // note, we passing in a flag to not keep
    // track of visited nodes, so we need to
    // keep track of visited nodes ourselves
    // because we need to know if a node has been processed
    this.traverseChildNodes(svgGraph, startIds, node => {
      if (processedNodes.has(node.id)) {
        // stop traversal of this branch
        // if the node has already been processed
        return 1
      }
      if (node.parents[0] !== 'start' && !processedNodes.has(node.parents[0])) {
        // stop traversal of this branch
        // if the parent has not been processed yet
        // because then then this node will be processed
        // after the parent has been processed
        return 1
      }
      const nodeX = node.x
      if (node.groupId && svgGroups?.[node.groupId]?.startSlotX > 0) {
        // if the node has a groupId, then always align with the group
        node.x = positionHelpers.getXPointFromSlot(svgGroups[node.groupId].startSlotX)
      } else if (svgGraph[node.parents[0]]) {
        // if the child doesn't have a groupId, then align with its parent
        node.x = svgGraph[node.parents[0]].x + 160
        // if a node is moved right and by doing so it enters a new group, then move the group right
        if (node.x > nodeX) {
          const group = positionHelpers.findGroupStartingAtCoordinate(node.x, svgGroups)
          if (group && group.startSlotX !== 0) {
            dragFunctions.moveGroupRight(group, svgGroups)
          }
        }
      } else if (node.parents[0] === 'start') {
        // if the parent is the start node, then align with the start node
        node.x = 80
      }
      if (node.x !== nodeX) {
        // the child has been moved
        // collect the ids of all moved nodes and return
        movedNodeIds.add(node.id)
      }
      processedNodes.add(node.id)
    }, false)
    return movedNodeIds
  },

  findCollision (svgGraph, node, laneId = null) {
    for (const n in svgGraph) {
      if (svgGraph[n].placeholder !== true &&
        svgGraph[n].x === node.x &&
        svgGraph[n].y === node.y &&
        svgGraph[n].id !== node.id &&
        (laneId === null || node.laneId === svgGraph[n].laneId) &&
        (!workflowApiHelpers.isMainParent(svgGraph, node.id, svgGraph[n].id) && !workflowApiHelpers.isMainParent(svgGraph, svgGraph[n].id, node.id))) {
        return svgGraph[n]
      }
    }
    return null
  },

  moveDownNode (node, svgGraph, svgLanes) {
    node.offset += 1
    node.y += 156
    if (!workflowApiHelpers.isNodeWithinLaneBoundaries(node, svgLanes)) {
      svgLanes[node.laneId].height = (node.offset + 1) * 156
      workflowApiHelpers.updateLane(svgLanes[node.laneId], svgGraph, svgLanes)
    }
  },

  highestAndLowestNode (svgGraph, node1, node2) {
    const node1Parent = svgGraph[node1.parents[0]] || node1
    const node2Parent = svgGraph[node2.parents[0]] || node2
    if (node1Parent.y < node2Parent.y) {
      return { highestNode: node1, lowestNode: node2 }
    }
    if (node1Parent.y > node2Parent.y) {
      return { highestNode: node2, lowestNode: node1 }
    }
    if (node1Parent.y === node2Parent.y) {
      if (node1.y > node1Parent.y) {
        if (node1Parent.x < node2Parent.x) {
          return { highestNode: node2, lowestNode: node1 }
        } else {
          return { highestNode: node1, lowestNode: node2 }
        }
      } else {
        if (node1Parent.x < node2Parent.x) {
          return { highestNode: node1, lowestNode: node2 }
        } else {
          return { highestNode: node2, lowestNode: node1 }
        }
      }
    }
  },

  updateVerticalPositionsIfCollision (svgGraph, svgLanes, movedNodeId, forceMoveOtherShapes = false) {
    const movedNode = svgGraph[movedNodeId]
    if (movedNode) {
      let targetNode
      if ((targetNode = workflowApiHelpers.findCollision(svgGraph, movedNode))) {
        if (forceMoveOtherShapes) {
          workflowApiHelpers.moveDownNode(targetNode, svgGraph, svgLanes)
          this.updateVerticalPositionsIfCollision(svgGraph, svgLanes, targetNode.id)
          this.updateVerticalPositionsIfCollision(svgGraph, svgLanes, movedNode.id)
        } else {
          const { highestNode, lowestNode } = this.highestAndLowestNode(svgGraph, movedNode, targetNode)
          workflowApiHelpers.moveDownNode(lowestNode, svgGraph, svgLanes)
          this.updateVerticalPositionsIfCollision(svgGraph, svgLanes, lowestNode.id)
          this.updateVerticalPositionsIfCollision(svgGraph, svgLanes, highestNode.id)
        }
      }
      if (workflowApiHelpers.isNodeCrossedByPath(movedNode, svgGraph)) {
        workflowApiHelpers.moveDownNode(movedNode, svgGraph, svgLanes)
        const allIds = Object.keys(svgGraph)
        allIds.forEach(id => this.updateVerticalPositionsIfCollision(svgGraph, svgLanes, id))
      }
    }
  },

  // Performs a depth-first traversal on a graph represented by an object.
  // Traversal starts from the nodes in the stackOfIds and uses a callback
  // to control traversal behavior:
  //   - callback(node) returns 0 or undefined: continue traversal.
  //   - callback(node) returns 1: stop traversal of this branch.
  //   - callback(node) returns 2: completely stop traversal.
  traverseChildNodes (svgGraph, stackOfIds, callback, keepTrackOfVisited = true) {
    if (!Array.isArray(stackOfIds)) {
      stackOfIds = [stackOfIds]
    }
    const visited = new Set()

    while (stackOfIds.length > 0) {
      const currentId = stackOfIds.shift()
      const currentNode = svgGraph[currentId]

      if (keepTrackOfVisited) {
        if (visited.has(currentId)) continue
        visited.add(currentId)
      }

      const callbackResult = callback(currentNode)

      if (callbackResult === 2) return // completely stop traversal
      if (callbackResult === 1) continue // stop traversal of this branch, but continue with others

      if (currentNode.childIds) {
        for (const childId of currentNode.childIds) {
          if (!visited.has(childId)) {
            stackOfIds.push(childId)
          }
        }
      }
    }
  },

  findPreferredOffset (shape, svgGraph, svgLanes, svgGroups, preferredOffset = null, isParallelInsertion = false) {
    const parentId = shape?.parents?.[0]
    const parentNode = parentId && svgGraph[parentId]

    // Determine the correct target X-coordinate for the new shape.
    let targetX
    if (shape.groupId && svgGroups[shape.groupId]) {
      targetX = positionHelpers.getXPointFromSlot(svgGroups[shape.groupId].startSlotX)
    } else if (parentNode) {
      targetX = parentNode.x + 160
    } else {
      targetX = 80
    }

    // Find events sharing the correct target space using the correct targetX.
    const eventsSharingSameSpace = Object.values(svgGraph)
      .filter((node) => (node.x === targetX) && (node.laneId === shape.laneId))

    // Determine the starting offset for the search.
    let offset = 0
    if (preferredOffset !== null && preferredOffset !== undefined) {
      // Use the caller's target row; the loop below still bumps it on collision.
      offset = Math.max(0, preferredOffset)
    } else {
      if (parentNode && parentNode.laneId === shape.laneId) {
        offset = parentNode.offset
      }

      // This logic is for inserting a node between a parent and its tightly-packed child.
      // Skipped for a parallel insertion, where the new node is a sibling branch, not threaded in between.
      if (parentNode && parentNode.type !== 'bpmn:ExclusiveGateway' && !isParallelInsertion && parentNode?.childIds?.length > 0) {
        if (!workflowApiHelpers.hasGapBetween(svgGraph, parentId, parentNode.childIds[0], 1)) {
          if (
            workflowApiHelpers.offsetOccupied(svgGraph, offset, parentId, shape.laneId, eventsSharingSameSpace)
          ) {
            offset = svgGraph[parentNode.childIds[0]].offset
          }
        }
      }
    }

    // Preferred offset found, now update it if needed and make space for it
    while (
      workflowApiHelpers.offsetOccupied(svgGraph, offset, parentId, shape.laneId, eventsSharingSameSpace, isParallelInsertion) ||
      workflowApiHelpers.isNodeCrossedByPath(
        {
          parentIds: [parentId],
          x: targetX,
          y: positionHelpers.getYPointFromOffset(offset, shape.laneId, svgLanes)
        },
        svgGraph
      )
    ) {
      offset++
    }

    workflowApiHelpers.createVerticalSpaceForOffset(
      offset,
      eventsSharingSameSpace || null,
      parentId,
      svgGraph,
      svgLanes
    )

    return offset
  },

  insertShapeIntoSvgGraph (shape, svgGraph, svgLanes, svgGroups, options = {}) {
    const parentNode = svgGraph[shape.parents[0]]
    const isParallelInsertion = options.parallel === true
    // On paste, keep the shape's original row relative to its parent.
    const preferredOffset = (
      options.offsetDeltaFromParent !== undefined &&
      options.offsetDeltaFromParent !== null &&
      parentNode &&
      parentNode.laneId === shape.laneId
    )
      ? parentNode.offset + options.offsetDeltaFromParent
      : null
    shape.offset = workflowApiHelpers.findPreferredOffset(shape, svgGraph, svgLanes, svgGroups, preferredOffset, isParallelInsertion)
    shape.y = svgLanes[shape.laneId].y + 30 + shape.offset * 156

    // If a groupId is provided, use it to determine the x-coordinate.
    if (shape.groupId && svgGroups[shape.groupId]) {
      shape.x = positionHelpers.getXPointFromSlot(svgGroups[shape.groupId].startSlotX)
    } else {
      shape.x = parentNode ? parentNode.x + 160 : 80
    }

    // For parallel insertion, don't adopt orphan children (like gateway behavior)
    const skipOrphanAdoption = parentNode?.type === 'bpmn:ExclusiveGateway' || isParallelInsertion
    shape.childIds = skipOrphanAdoption ? undefined : (parentNode ? parentNode.childIds : undefined)
    shape.placeHolder = true
    shape.selectEventText = true

    // Preserve the incoming groupId. Only set to null if one wasn't provided.
    shape.groupId = shape.groupId || null

    shape.outlined = true
    shape.showConditionLabelOnEvent = false
    shape.margin = shape.type === 'bpmn:ExclusiveGateway' ? 33 : 0
    shape.id = shape.id || randomUUID() // if we copy paste a node, then we already have an id at this point
    svgGraph[shape.id] = shape

    // if the new shape is outside the lane boundary, increase the lane
    // height by one unit and trigger an update
    if (!workflowApiHelpers.isNodeWithinLaneBoundaries(shape, svgLanes)) {
      svgLanes[shape.laneId].height = (shape.offset + 1) * 156
      workflowApiHelpers.updateLane(svgLanes[shape.laneId], svgGraph, svgLanes)
    }

    if (!parentNode) {
      // if this it's the first event, we don't need
      // to align other parts of the diagram
      return
    }

    // update parent and child references
    // For parallel insertion, skip replacing parent (like gateway behavior)
    if (!skipOrphanAdoption) {
      // the new shape takes over the slot the children were in, so it adopts all
      // of them, the same way deleting it hands them all back to the parent
      for (const childId of [...(parentNode?.childIds || [])]) {
        dragFunctions.replaceParent(svgGraph[childId], parentNode, svgGraph[shape.id])
      }
    }
    dragFunctions.recomputeChildIds(svgGraph)

    // align all nodes in the diagram on the x-axis
    let movedNodeIds
    // For parallel insertion, use gateway-style alignment (branch layout)
    if (parentNode.type === 'bpmn:Task' && !isParallelInsertion) {
      // make room sideways
      movedNodeIds = workflowApiHelpers.updateEventsWithLongestPath(svgGraph, svgGroups)
      // adjustGroupPosition is done after updateEventsWithLongestPath because
      // the old value of the group divider is needed by updateEventsWithLongestPath
      this.adjustGroupPosition(shape, svgGroups)
      movedNodeIds.add(workflowApiHelpers.alignBranches(svgGraph, svgGroups))
    } else {
      this.adjustGroupPosition(shape, svgGroups)
      movedNodeIds = workflowApiHelpers.alignBranches(svgGraph, svgGroups)
      movedNodeIds.add(shape.id)
    }

    movedNodeIds.forEach((movedNodeId) => {
      workflowApiHelpers.updateVerticalPositionsIfCollision(svgGraph, svgLanes, movedNodeId)
    })
  },

  // move the group divider to the right if needed
  adjustGroupPosition (shape, svgGroups) {
    const group = positionHelpers.findGroupStartingAtCoordinate(shape.x, svgGroups)
    if (group && group.startSlotX !== 0) {
      dragFunctions.moveGroupRight(group, svgGroups)
    }
  },

  addNewShapeToSvgGraphAndAlignAllNodes (shape, options, svgLanes, svgGraph, svgGroups) {
    const parentNode = svgGraph[shape.parents[0]]
    if (!shape.laneId) {
      // new lane will be created
      shape.laneId = workflowApiHelpers.addLane(svgLanes, options.laneName)
    }
    const newShape = {
      id: shape.id || undefined,
      parents: shape.parents,
      type: shape.type,
      name: shape.description,
      laneId: shape.laneId,
      groupId: shape.groupId,
      subprocess: shape.subprocess || undefined,
      color: shape.color || parentNode?.color,
      cards: shape.cards,
      conditionLabels: shape.conditionLabels
    }
    workflowApiHelpers.insertShapeIntoSvgGraph(newShape, svgGraph, svgLanes, svgGroups, options)
    return newShape.id
  },

  getNextDescription (events, suggestedDescription = null) {
    if (!events) {
      events = this.events
    }
    if (!suggestedDescription) {
      suggestedDescription = 'New event'
    }
    let description = suggestedDescription
    let counter = 2
    while (events?.findIndex(wt => wt.description === description) >= 0) {
      description = suggestedDescription + ' ' + counter
      counter++
    }
    return description
  },

  getNextDecisionDescription (events) {
    let description = 'New decision'
    let counter = 2
    while (events.findIndex(wt => wt.description === description) >= 0) {
      description = 'New decision ' + counter
      counter++
    }
    return description
  },

  refreshDiagram (svgGraph, svgLanes, svgGroups) {
    if (svgGraph) {
      // console.log('this.svgGraph BEFORE', JSON.parse(JSON.stringify(svgGraph)))
      workflowApiHelpers.updateEventsWithLongestPath(svgGraph, svgGroups)
      // console.log('this.svgGraph AFTER', JSON.parse(JSON.stringify(svgGraph)))
      const allIds = Object.keys(svgGraph)
        .sort((a, b) => {
          const nodeA = svgGraph[a]
          const nodeB = svgGraph[b]

          const laneA = svgLanes[nodeA.laneId]
          const laneB = svgLanes[nodeB.laneId]

          return laneB.y - laneA.y
        })
      allIds.forEach(id => workflowApiHelpers.updateVerticalPositionsIfCollision(svgGraph, svgLanes, id))
    }
  },

  refreshDiagramPartially (svgGraph, svgLanes, svgGroups, { draggedNode }, selectedElement) {
    const draggedNodeX = draggedNode?.x
    const movedNodeIds = workflowApiHelpers.updateEventsWithLongestPath(svgGraph, svgGroups, false)
    // the reflow reports what it reparented, not what it only slid into another column,
    // so pick up whatever ended up sharing a cell with something else
    const occupiedCells = new Set()
    Object.values(svgGraph).forEach((node) => {
      const cell = node.x + ',' + node.y
      if (occupiedCells.has(cell)) {
        movedNodeIds.add(node.id)
      } else {
        occupiedCells.add(cell)
      }
    })
    movedNodeIds.forEach((movedNodeId) => {
      if (movedNodeId !== selectedElement.id) {
        workflowApiHelpers.updateVerticalPositionsIfCollision(svgGraph, svgLanes, movedNodeId)
      }
    })
    // growing a lane replaces the node objects of the lanes below it, draggedNode can be stale
    const refreshedDraggedNode = svgGraph[draggedNode?.id]
    if (draggedNodeX && refreshedDraggedNode) {
      refreshedDraggedNode.x = draggedNodeX
    }

    return refreshedDraggedNode
  },

  countNodes (parentIds, parentId = 'start') {
    let counter = 0
    for (const event of parentIds[parentId] || []) {
      if (parentIds[event.id]) {
        counter = counter + 1 + this.countNodes(parentIds, event.id)
      } else {
        counter++
      }
    }
    return counter
  },

  getRemovableSequenceFlows (events, eventId) {
    const res = []
    const eventsWithMoreThanOneParent = events.filter(wt => wt.parents && wt.parents.length > 1)
    const childrenOfSelectedEvent = eventsWithMoreThanOneParent.filter(wt => wt.parents.includes(eventId))
    for (let i = 0; i < childrenOfSelectedEvent.length; i++) {
      const index = events.findIndex(wt => wt.id === childrenOfSelectedEvent[i].id)
      const tmpEvents = JSON.parse(JSON.stringify(events))
      tmpEvents[index].parents = tmpEvents[index].parents.filter(parent => parent !== eventId)
      const tmpParentIdMatrix = dataHelpers.createMainParentChildEventMap(tmpEvents)
      const countNodes = this.countNodes(tmpParentIdMatrix)
      if (countNodes === tmpEvents.length) {
        res.push(childrenOfSelectedEvent[i])
      }
    }
    return res
  },

  transferGroupToChildIfAnchor (eventNode, child, svgGraph, svgGroups) {
    if (!eventNode.groupId || !child || child.groupId || eventNode.x >= child.x) {
      return
    }
    const childGroup = positionHelpers.getGroupFromXCoordinate(child.x, svgGroups)
    const otherParentsInSameGroup = dragFunctions
      .getParentsInTargetGroup(child, eventNode.groupId, svgGraph, svgGroups, true, false)
      .filter((parentId) => parentId !== eventNode.id)
    if (childGroup?.id === eventNode.groupId && otherParentsInSameGroup.length === 0) {
      child.groupId = eventNode.groupId
    }
  },

  deleteEventFromDiagram (eventId, svgGraph, svgGroups) {
    const eventNode = svgGraph[eventId]
    const childNode = svgGraph[eventNode.childIds?.[0]]
    let parentNode = svgGraph[eventNode.parents?.[0]]

    this.transferGroupToChildIfAnchor(eventNode, childNode, svgGraph, svgGroups)

    // if there is another parent, then fetch it
    // otherwise replaceParent will add 'start' as parent
    if (!parentNode) {
      parentNode = dragFunctions.findOtherStartingPoint(childNode, eventNode.id, svgGraph, true)
      if (parentNode?.id === childNode?.id) {
        // if the parent is the same as the child
        // then it's not a real parent
        parentNode = null
      }
    }

    dragFunctions.replaceParent(childNode, eventNode, parentNode)
    workflowApiHelpers.replaceChild(parentNode, eventNode, childNode)
    if (parentNode && childNode) {
      this.removeGroupFromChildIfSameGroup(parentNode.id, childNode.id, svgGraph, svgGroups)
    }

    // Reconnect any additional children (parallel branches from a decision or
    // parallel-event) to the same parent so they don't become orphaned.
    const otherChildIds = (eventNode.childIds || []).slice(1)
    for (const otherChildId of otherChildIds) {
      const otherChildNode = svgGraph[otherChildId]
      if (!otherChildNode) {
        continue
      }
      this.transferGroupToChildIfAnchor(eventNode, otherChildNode, svgGraph, svgGroups)
      dragFunctions.replaceParent(otherChildNode, eventNode, parentNode)
      if (parentNode) {
        if (!parentNode.childIds) {
          parentNode.childIds = []
        }
        if (!parentNode.childIds.includes(otherChildId)) {
          parentNode.childIds.push(otherChildId)
        }
        this.removeGroupFromChildIfSameGroup(parentNode.id, otherChildId, svgGraph, svgGroups)
      }
    }

    const otherParentIds = eventNode.parents.slice(1) // create a copy and remove the first parent (parent[0]) in the copy
    for (const otherParentId of otherParentIds) {
      // remove extra connections to the event about to be deleted
      svgGraph[otherParentId].childIds = svgGraph[otherParentId].childIds.filter(childId => childId !== eventId)
    }

    delete svgGraph[eventId]
  },

  deleteEventsFromDiagram (eventIds, svgGraph, svgGroups) {
    const deleteEventIds = new Set(eventIds)

    while (deleteEventIds.size > 0) {
      // Find nodes that have no more than 1 child (0 or 1 children) - these can be safely deleted
      const deletableNodes = Array.from(deleteEventIds).filter(eventId => {
        const node = svgGraph[eventId]
        if (!node) return true

        // Only delete nodes with 0 or 1 child total
        const childCount = node.childIds ? node.childIds.length : 0
        return childCount <= 1
      })

      // If no deletable nodes found, handle loops by deleting the lexicographically larger node first
      if (deletableNodes.length === 0) {
        // Find any remaining nodes and delete the one with the largest ID (child in loop)
        const remainingNodes = Array.from(deleteEventIds)
        const nodeToDelete = remainingNodes.reduce((max, current) =>
          current > max ? current : max
        )
        deletableNodes.push(nodeToDelete)
      }

      // Delete all deletable nodes found in this iteration
      deletableNodes.forEach(eventId => {
        if (deleteEventIds.has(eventId)) {
          this.deleteEventFromDiagram(eventId, svgGraph, svgGroups)
          deleteEventIds.delete(eventId)
        }
      })
    }
  },

  // the function removes 'start' from targetEventId's parents array
  // if the event is not connected through a loop or from another group
  // located to the right of the current event
  // so that we always can strech out diagrams to the right as much as possible
  removeStartingPointIfNotConnected (targetEventId, svgGraph, svgGroups) {
    // console.log('targetEventId', targetEventId)
    // console.log('svgGraph', JSON.parse(JSON.stringify(svgGraph)))
    // console.log('svgGroups', JSON.parse(JSON.stringify(svgGroups)))
    if (svgGraph[targetEventId].parents.findIndex(parentId => parentId === 'start') === -1) {
      return
    }
    if (svgGraph[targetEventId].parents.length <= 1) {
      return
    }
    const parentIds = svgGraph[targetEventId].parents
    let removeStartingPoint
    // do the check for each parent of the target event
    for (const parentId of parentIds.filter(id => id !== 'start')) {
      removeStartingPoint = true
      // Keep starting point only if it's connected via a loop inside the same group
      // or if it's connected back from another group
      const targetGroup = workflowApiHelpers.getGroupFromNode(svgGraph[targetEventId], svgGroups, svgGraph)
      // if (targetGroup) {
      if (dragFunctions.hasAncestorDescendantRelationshipInsideGroup(targetEventId, parentId, targetGroup?.id, svgGraph, svgGroups)) {
        removeStartingPoint = false
      }
      // }
      if (removeStartingPoint && Object.keys(svgGroups)?.length > 0) {
        const targetGroup = workflowApiHelpers.getGroupFromNode(svgGraph[targetEventId], svgGroups, svgGraph)
        const currentGroup = workflowApiHelpers.getGroupFromNode(svgGraph[parentId], svgGroups, svgGraph)
        if (targetGroup?.id !== currentGroup?.id && targetGroup.startSlotX < currentGroup.startSlotX) {
          removeStartingPoint = false
        }
      }
      // if there is a connection from left or same column to the targetEventId
      // then always remove the starting point
      if (svgGraph[parentId].x <= svgGraph[targetEventId].x) {
        removeStartingPoint = true
      }
      if (removeStartingPoint) {
        break
      }
    }
    if (removeStartingPoint) {
      this.removeStartingPointFromNode(svgGraph[targetEventId])
    }
    // console.log('svgGraph AFTER', JSON.parse(JSON.stringify(svgGraph)))
  },

  removeStartingPointFromNode (node) {
    node.parents = node.parents.filter(parent => parent !== 'start')
  },

  // removes the connection from the parent
  // to the child and updates svgGraph
  deleteConnectionFromEvent (parentId, childId, svgGraph, svgGroups) {
    if (!parentId || !childId) {
      return
    }
    const parentEvent = svgGraph[parentId]
    const childEvent = svgGraph[childId]
    const parentIndex = childEvent?.parents.findIndex(parent => parent === parentId)
    // remove parent from child
    childEvent.parents.splice(parentIndex, 1)
    // drop the branch label the child carried for the removed connection
    if (childEvent.conditionLabels?.length) {
      const remaining = childEvent.conditionLabels.filter((label) => label.parentId !== parentId)
      childEvent.conditionLabels = remaining.length ? remaining : null
    }
    // set or remove start node on child if needed
    dragFunctions.updateStartNodeStatus(childEvent, svgGraph, svgGroups)
    // remove child from parent
    let index = parentEvent.childIds.indexOf(childEvent.id)
    if (index >= 0) {
      index = parentEvent.childIds.splice(index, 1)
    }

    // add group based on slot x
    if (Object.keys(svgGroups).length > 0) {
      const childGroup = positionHelpers.getGroupFromXCoordinate(childEvent.x, svgGroups)
      // let hasParentsInSameGroup = false
      const parentsInTargetGroupLeftSide = dragFunctions.getParentsInTargetGroup(childEvent, childGroup.id, svgGraph, svgGroups, true, false)
      // parentsInTargetGroupLeftSide?.forEach(parentId => {
      //   // next row looks like a bug, because it's passing in parentId as groupId
      //   console.log('###############################')
      //   console.log('parentId', parentId)
      //   if (dragFunctions.hasAncestorDescendantRelationshipInsideGroup(childEvent.id, parentId, childGroup.id, svgGraph, svgGroups)) {
      //     // it's a loopback connection
      //   } else {
      //     // no loopback connection
      //     hasParentsInSameGroup = true
      //   }
      // })
      if (parentsInTargetGroupLeftSide.length > 0 || childGroup.startSlotX === 0) {
        // if the child has parent 0 in the same group as itself then don't add a group
        // or if the group is the first group, then don't add a group
        childEvent.groupId = null
      } else {
        childEvent.groupId = childGroup.id
      }
    }

    for (const eventId in svgGraph) {
      if (svgGraph[eventId].parents[0] === 'start') {
        workflowApiHelpers.removeStartingPointIfNotConnected(eventId, svgGraph, svgGroups)
      }

      // remove group from node if it has a group but
      // now has a parent in the same group that
      // is not a decendant of the node in an
      // unbroken chain inside the group
      // for example when there is a circle of events
      // inside a group and one of the events in the middle
      // is disconnected
      const childNode = svgGraph[eventId]
      if (childNode.groupId) {
        const parentsInTargetGroup = dragFunctions.getParentsInTargetGroup(childNode, childNode.groupId, svgGraph, svgGroups)
        parentsInTargetGroup?.forEach(parentId => {
          if (dragFunctions.hasAncestorDescendantRelationshipInsideGroup(childNode.id, parentId, childNode.groupId, svgGraph, svgGroups)) {
            // don't update the group id
          } else {
            // remove groupId because we have a parent in the same group
            // that is not a child of the node
            childNode.groupId = null
          }
        })
      }
    }
  },

  // When a node stops being a gateway (e.g. transformed to an event), its
  // children no longer have a branch condition for that connection. Drop those
  // labels while keeping any labels the child carries for other parents.
  clearChildConditionLabelsForParent (svgGraph, parentId) {
    const parent = svgGraph[parentId]
    if (!parent || !parent.childIds) {
      return
    }
    for (const childId of parent.childIds) {
      const child = svgGraph[childId]
      if (!child) {
        continue
      }
      if (child.conditionLabels?.length) {
        const remaining = child.conditionLabels.filter((label) => label.parentId !== parentId)
        child.conditionLabels = remaining.length ? remaining : null
      }
      if (child.parents?.[0] === parentId) {
        child.showConditionLabelOnEvent = false
      }
    }
  },

  // adds a connection from the sourceEvent
  // to the targetEvent, updates svgGraph
  addConnectionToEvent (sourceEventId, targetEventId, svgGraph, svgGroups) {
    if (!sourceEventId || !targetEventId) {
      return
    }
    svgGraph[targetEventId].parents.push(sourceEventId)
    if (!svgGraph[sourceEventId].childIds) {
      svgGraph[sourceEventId].childIds = []
    }
    svgGraph[sourceEventId].childIds.push(targetEventId)
    workflowApiHelpers.removeStartingPointIfNotConnected(targetEventId, svgGraph, svgGroups)

    this.removeGroupFromChildIfSameGroup(sourceEventId, targetEventId, svgGraph, svgGroups)
  },

  // if the child and the parents are connected and
  // in the same group, then remove the group from the child
  // so that the child is not displayed in the same slot as the parent
  removeGroupFromChildIfSameGroup (parentId, childId, svgGraph, svgGroups) {
    const parentGroup = positionHelpers.getGroupFromXCoordinate(svgGraph[parentId].x, svgGroups)
    const childGroup = positionHelpers.getGroupFromXCoordinate(svgGraph[childId].x, svgGroups)
    // if (svgGraph[childId].parents[0] === parentId && parentGroup?.id === childGroup?.id) {

    if (parentGroup && parentGroup?.id === childGroup?.id && parentId !== childId) {
      const childIsAlsoParent = dragFunctions.hasAncestorDescendantRelationshipInsideGroup(childId, parentId, parentGroup.id, svgGraph, svgGroups)
      if (!childIsAlsoParent || svgGraph[parentId].x <= svgGraph[childId].x) {
        svgGraph[childId].groupId = null
      }
    }
  },

  // Deletes a group and every event inside it, bridges the events on its left to
  // those on its right, and slides the remaining groups/events left to close the gap.
  deleteGroup (groupId, svgGraph, svgGroups) {
    const groupToDelete = svgGroups[groupId]
    if (!groupToDelete) {
      return
    }

    const startSlotX = groupToDelete.startSlotX
    const sortedGroups = Object.values(svgGroups).sort((a, b) => a.startSlotX - b.startSlotX)
    const groupIndex = sortedGroups.findIndex((group) => group.id === groupId)
    const nextGroup = sortedGroups[groupIndex + 1]

    // 1. Collect events inside the group by slot range (first-group events have a null groupId).
    const memberEventIds = Object.keys(svgGraph).filter((eventId) => {
      const slotX = positionHelpers.getSlotXFromPoint(svgGraph[eventId].x)

      return slotX >= startSlotX && (!nextGroup || slotX < nextGroup.startSlotX)
    })

    // 2. Delete them; deleteEventsFromDiagram bridges each event's external parent to its external child.
    this.deleteEventsFromDiagram(memberEventIds, svgGraph, svgGroups)

    // 2b. Safety net: drop any stale reference to a deleted event so it can't collide
    // with that event's removal in the update payload (DynamoDB "document paths overlap").
    const deletedIds = new Set(memberEventIds)
    for (const eventId in svgGraph) {
      const node = svgGraph[eventId]
      const cleanedParents = node.parents.filter((parentId) => !deletedIds.has(parentId))
      if (cleanedParents.length !== node.parents.length) {
        node.parents = cleanedParents.length > 0 ? cleanedParents : ['start']
      }
      if (node.childIds) {
        node.childIds = node.childIds.filter((childId) => !deletedIds.has(childId))
      }
    }

    // 3. Remove the group container itself.
    delete svgGroups[groupId]

    // 4. Close the gap: slide the groups/events right of the deleted one left by the removed span.
    if (nextGroup) {
      const nextGroupStartSlotX = nextGroup.startSlotX
      const slotShift = nextGroupStartSlotX - startSlotX
      for (const remainingGroupId in svgGroups) {
        if (svgGroups[remainingGroupId].startSlotX > startSlotX) {
          svgGroups[remainingGroupId].startSlotX -= slotShift
          svgGroups[remainingGroupId].x = positionHelpers.getGroupXPointFromSlot(svgGroups[remainingGroupId].startSlotX)
        }
      }
      for (const eventId in svgGraph) {
        if (positionHelpers.getSlotXFromPoint(svgGraph[eventId].x) >= nextGroupStartSlotX) {
          svgGraph[eventId].x -= slotShift * 160
        }
      }
    }

    // 5. Recompute group widths/indices and event groupIds from final positions.
    this.recomputeGroupLayout(svgGroups)
    this.updateGroupStartingPosition(svgGraph, svgGroups)
  },

  // Removes only the group container, keeping its events. Each event folds into
  // the neighbouring group by position (see the first-group special case below).
  deleteGroupKeepEvents (groupId, svgGraph, svgGroups) {
    const groupToDelete = svgGroups[groupId]
    if (!groupToDelete) {
      return
    }

    const sortedGroups = Object.values(svgGroups).sort((a, b) => a.startSlotX - b.startSlotX)
    const groupIndex = sortedGroups.findIndex((group) => group.id === groupId)

    // First group: promote the next group to slot 0 so its events stay grouped.
    const nextGroup = sortedGroups[groupIndex + 1]
    if (groupIndex === 0 && nextGroup) {
      nextGroup.startSlotX = 0
      nextGroup.x = positionHelpers.getGroupXPointFromSlot(0)
    }

    // Drop the divider; the calls below reassign events to the neighbouring group.
    delete svgGroups[groupId]

    this.recomputeGroupLayout(svgGroups)
    this.updateGroupStartingPosition(svgGraph, svgGroups)
  },

  // Recomputes each group's width/index from startSlotX order, matching the
  // svgGroupsData getter (first group wider via the lane-width offset, last is -1).
  recomputeGroupLayout (svgGroups) {
    const orderedGroups = Object.values(svgGroups).sort((a, b) => a.startSlotX - b.startSlotX)
    orderedGroups.forEach((group, index) => {
      const current = svgGroups[group.id]
      const nextGroupInOrder = orderedGroups[index + 1]
      current.index = index
      current.width = nextGroupInOrder
        ? positionHelpers.getGroupXPointFromSlot(nextGroupInOrder.startSlotX) - current.x
        : -1
    })
  },

  // Swaps a group with its right neighbour and severs the boundary connections
  // (entry events become start points, exit events lose their output).
  switchGroupWithRightNeighbor (leftGroupId, svgGraph, svgGroups) {
    const sortedGroups = Object.values(svgGroups).sort((a, b) => a.startSlotX - b.startSlotX)
    const aIndex = sortedGroups.findIndex((group) => group.id === leftGroupId)
    const groupA = sortedGroups[aIndex]
    const groupB = sortedGroups[aIndex + 1]
    if (!groupA || !groupB) {
      return
    }
    const groupC = sortedGroups[aIndex + 2]
    const slotA = groupA.startSlotX
    const slotB = groupB.startSlotX
    const slotC = groupC ? groupC.startSlotX : null

    const slotOf = (eventId) => positionHelpers.getSlotXFromPoint(svgGraph[eventId].x)
    const aEventIds = Object.keys(svgGraph).filter((id) => {
      const slot = slotOf(id)

      return slot >= slotA && slot < slotB
    })
    const bEventIds = Object.keys(svgGraph).filter((id) => {
      const slot = slotOf(id)

      return slot >= slotB && (slotC === null || slot < slotC)
    })
    const aSet = new Set(aEventIds)
    const bSet = new Set(bEventIds)

    // Sever connections crossing the two groups' boundaries; events left with no
    // parent become start points (exit events lose their output when childIds is rebuilt).
    const severParents = (eventId, isSeveredParent) => {
      const kept = svgGraph[eventId].parents.filter((parentId) => !isSeveredParent(parentId))
      if (kept.length !== svgGraph[eventId].parents.length) {
        svgGraph[eventId].parents = kept.length > 0 ? kept : ['start']
      }
    }
    // A's events: drop incoming edges from 'start' or any group to the left of A.
    for (const id of aEventIds) {
      severParents(id, (parentId) =>
        parentId === 'start' || (!aSet.has(parentId) && slotOf(parentId) < slotA))
    }
    // B's events: drop incoming edges from A.
    for (const id of bEventIds) {
      severParents(id, (parentId) => aSet.has(parentId))
    }
    // Events to the right of B: drop incoming edges from B.
    for (const id in svgGraph) {
      if (bSet.has(id) || aSet.has(id)) {
        continue
      }
      severParents(id, (parentId) => bSet.has(parentId) && slotC !== null && slotOf(id) >= slotC)
    }

    // Swap horizontal positions of the two groups and their events.
    const spanA = slotB - slotA
    const spanB = slotC !== null
      ? slotC - slotB
      : Math.max(slotB, ...bEventIds.map(slotOf)) - slotB + 1
    groupB.startSlotX = slotA
    groupB.x = positionHelpers.getGroupXPointFromSlot(slotA)
    groupA.startSlotX = slotA + spanB
    groupA.x = positionHelpers.getGroupXPointFromSlot(slotA + spanB)
    for (const id of aEventIds) {
      svgGraph[id].x += spanB * 160
    }
    for (const id of bEventIds) {
      svgGraph[id].x -= spanA * 160
    }

    // Recompute group widths/indices for the new order (avoids stale widths).
    this.recomputeGroupLayout(svgGroups)

    // Rebuild childIds from the rewired parents and recompute groupIds.
    for (const id in svgGraph) {
      svgGraph[id].childIds = []
    }
    for (const id in svgGraph) {
      for (const parentId of svgGraph[id].parents) {
        if (parentId !== 'start' && svgGraph[parentId]) {
          svgGraph[parentId].childIds.push(id)
        }
      }
    }
    this.updateGroupStartingPosition(svgGraph, svgGroups)
  },

  // Moves a group to targetIndex by switching it with the adjacent group one step
  // at a time, so every boundary it crosses is severed like a single switch.
  reorderGroup (groupId, targetIndex, svgGraph, svgGroups) {
    const orderedIndexOf = (id) => Object.values(svgGroups)
      .sort((a, b) => a.startSlotX - b.startSlotX)
      .findIndex((group) => group.id === id)

    let currentIndex = orderedIndexOf(groupId)
    if (currentIndex === -1) {
      return
    }
    const target = Math.max(0, Math.min(targetIndex, Object.keys(svgGroups).length - 1))

    while (currentIndex < target) {
      this.switchGroupWithRightNeighbor(groupId, svgGraph, svgGroups)
      currentIndex++
    }
    while (currentIndex > target) {
      const leftNeighbor = Object.values(svgGroups)
        .sort((a, b) => a.startSlotX - b.startSlotX)[currentIndex - 1]
      if (!leftNeighbor) {
        break
      }
      this.switchGroupWithRightNeighbor(leftNeighbor.id, svgGraph, svgGroups)
      currentIndex--
    }
  },

  updateGroupStartingPosition (svgGraph, svgGroups) {
    for (const eventId in svgGraph) {
      const event = svgGraph[eventId]
      const group = positionHelpers.getGroupFromXCoordinate(svgGraph[eventId].x, svgGroups)
      event.groupId = null
      if (!group || group.startSlotX === 0) {
        // all events in the first group should have groupId set to null
        event.groupId = null
      } else {
        const parentsInTargetGroup = dragFunctions.getParentsInTargetGroup(event, group.id, svgGraph, svgGroups, false, false)
        if (parentsInTargetGroup.length === 0) {
          event.groupId = group.id
        } else {
          parentsInTargetGroup?.every(parentId => {
            if (dragFunctions.hasAncestorDescendantRelationshipInsideGroup(event.id, parentId, group.id, svgGraph, svgGroups)) {
              if (event.x < svgGraph[parentId].x) {
                event.groupId = group.id
                return true
              } else {
                return false
              }
            } else {
              event.groupId = null
              return false
            }
          })
        }
      }
    }
  },

  /**
   * Get the width of a group by slot unit.
   * If 2 events/decisions are aligned (same slot/column), they count as 1 slot.
   * @param {string} groupId - The group ID to get the width for
   * @param {Object} svgGraph - The graph containing all nodes
   * @param {Object} svgGroups - The groups object
   * @returns {number} - The width of the group in slot units
   */
  getGroupWidthBySlot (groupId, svgGraph, svgGroups) {
    if (!groupId || !svgGroups || !svgGroups[groupId]) {
      return 0
    }

    const group = svgGroups[groupId]
    const groupStartSlotX = group.startSlotX

    // Find the next group's startSlotX to determine the boundary
    const sortedGroups = Object.values(svgGroups).sort((a, b) => a.startSlotX - b.startSlotX)
    const groupIndex = sortedGroups.findIndex(g => g.id === groupId)
    const nextGroup = sortedGroups[groupIndex + 1]

    // Collect all unique slot positions occupied by events in this group
    const occupiedSlots = new Set()

    for (const eventId in svgGraph) {
      const event = svgGraph[eventId]
      const eventSlotX = positionHelpers.getSlotXFromPoint(event.x)

      // Check if the event is within this group's boundaries
      const isInGroup = eventSlotX >= groupStartSlotX &&
        (!nextGroup || eventSlotX < nextGroup.startSlotX)

      if (isInGroup) {
        occupiedSlots.add(eventSlotX)
      }
    }

    const width = occupiedSlots.size
    return width
  },

  /**
   * Get the most right event/decision in a group which is connected to the target event.
   * Returns the rightmost descendant of the target event within the specified group.
   * @param {string} eventId - The target event ID
   * @param {string} groupId - The group ID to search within
   * @param {Object} svgGraph - The graph containing all nodes
   * @param {Object} svgGroups - The groups object
   * @returns {Object|null} - The most right event/decision node, or null if not found
   */
  getMostRightDescendantInGroup (eventId, groupId, svgGraph, svgGroups) {
    if (!eventId || !groupId || !svgGraph || !svgGraph[eventId] || !svgGroups || !svgGroups[groupId]) {
      return null
    }

    const targetGroup = svgGroups[groupId]
    const groupStartSlotX = targetGroup.startSlotX

    // Find the next group's startSlotX to determine the boundary
    const sortedGroups = Object.values(svgGroups).sort((a, b) => a.startSlotX - b.startSlotX)
    const groupIndex = sortedGroups.findIndex(g => g.id === groupId)
    const nextGroup = sortedGroups[groupIndex + 1]

    let mostRightDescendant = null
    let maxSlotX = -1

    // Traverse all descendants of the target event
    workflowApiHelpers.traverseChildNodes(svgGraph, eventId, (descendant) => {
      // Skip the source event itself
      if (descendant.id === eventId) {
        return 0 // continue
      }

      const descendantSlotX = positionHelpers.getSlotXFromPoint(descendant.x)

      // Check if the descendant is within this group's boundaries
      const isInGroup = descendantSlotX >= groupStartSlotX &&
        (!nextGroup || descendantSlotX < nextGroup.startSlotX)

      if (isInGroup) {
        if (descendantSlotX > maxSlotX) {
          maxSlotX = descendantSlotX
          mostRightDescendant = descendant
        }
      } else if (nextGroup && descendantSlotX >= nextGroup.startSlotX) {
        // Stop traversing this branch if we've left the group (going right)
        return 1
      }

      return 0 // continue
    })

    return mostRightDescendant
  },

  /**
   * Determines if a node should have a groupId based on its position and parent relationships
   * @param {Object} node - The node to check
   * @param {Object} svgGraph - The graph containing all nodes
   * @param {Object} svgGroups - The groups object
   * @returns {string|null} - The groupId if node should have one, null otherwise
   */
  shouldHaveGroupId (node, svgGraph, svgGroups) {
    const nodeSlotX = positionHelpers.getSlotXFromId(node.id, svgGraph)
    const nodeGroup = positionHelpers.getGroupFromSlotX(nodeSlotX, svgGroups)

    // If node is in first group (slot 0) or no group, groupId should be null
    if (!nodeGroup || nodeGroup.startSlotX === 0) {
      return null
    }

    // Check if this node has parents in the target group
    const parentsInTargetGroup = dragFunctions.getParentsInTargetGroup(
      node,
      nodeGroup.id,
      svgGraph,
      svgGroups,
      false,
      false
    )

    // If no parents in target group, it's the start of the group -> assign groupId
    if (parentsInTargetGroup.length === 0) {
      return nodeGroup.id
    }

    // Has parents in the group -> groupId should be null
    return null
  },

  findUpdatedSchemas (newSchemas, oldSchemas) {
    const oldSchemasArr = oldSchemas || []
    const newSchemasArr = newSchemas || []

    const schemaUpdates = []
    const deletedSchemas = []

    const oldSchemaMap = new Map()
    oldSchemasArr.forEach((schema, index) => {
      if (schema.id) {
        oldSchemaMap.set(schema.id, { schema, index })
      }
    })

    const newSchemaIds = new Set()
    newSchemasArr.forEach((schema) => {
      if (schema.id) {
        newSchemaIds.add(schema.id)
      }
    })

    for (const [id, { index }] of oldSchemaMap) {
      if (!newSchemaIds.has(id)) {
        deletedSchemas.push({ deleteIndex: index, deleteId: id })
      }
    }

    for (const newSchema of newSchemasArr) {
      if (!newSchema.id) {
        continue
      }
      const oldEntry = oldSchemaMap.get(newSchema.id)
      if (!oldEntry) {
        // New schema — append with index -1
        schemaUpdates.push({
          ...newSchema,
          index: -1
        })
        continue
      }

      const { schema: oldSchema, index } = oldEntry
      const { version: oldVersion, ...oldComparable } = oldSchema
      const { version: _, ...newComparable } = newSchema

      if (JSON.stringify(oldComparable) !== JSON.stringify(newComparable)) {
        schemaUpdates.push({
          ...newSchema,
          index,
          expectedVersion: oldVersion || null
        })
      }
    }

    if (schemaUpdates.length === 0 && deletedSchemas.length === 0) {
      return null
    }

    return { schemaUpdates, deletedSchemas }
  }
}

/* *************** */
/* local functions */
/* *************** */
function equalConditionLabels (oldConditionLabels, newConditionLabels) {
  if ((oldConditionLabels?.length || 0) !== (newConditionLabels?.length || 0)) {
    return false
  }
  for (let i = 0; i < (oldConditionLabels?.length || 0); i++) {
    if (oldConditionLabels[i].name !== newConditionLabels[i].name ||
      oldConditionLabels[i].parentId !== newConditionLabels[i].parentId) {
      return false
    }
  }
  return true
}
